import { useRef, useState, useEffect, lazy, Suspense } from "react";

import { html } from '@/utils/Format';
import { date } from '@/utils/Dates';

const Tabs = lazy(() => import('@mui/material/Tabs'));
const Tab = lazy(() => import('@mui/material/Tab'));

import { styleTabs } from '@/utils/Styles';

const Table = lazy(() => import('/src/components/Table'));

const ManageIcon = lazy(() => import('@mui/icons-material/ManageAccounts'));
const ListIcon = lazy(() => import('@mui/icons-material/FormatListBulleted'));

const NotificationModal = lazy(() => import('/src/modals/NotificationModal'));

const Notification = lazy(() => import('/src/pages/Notifications/Notification'));

import Button from '@/components/Button';

import { checkViewItem } from '@/utils/Table';
import { getCurrentTab, getNewUrl } from '@/utils/Tabs';

export default function Notifications({ t, language, user, token, handleLogin, router, apiUrl, 
    getData, setData, deleteData, sendMessage, fullScreen, fullPage, setFullPage,openUrlModal
}) {

    const [ api, setApi ] = useState(null);

    const containerRef = useRef(null);

    const [ table ] = useState('notifications');

    // response from the api when getting data
    const [ response, setResponse ] = useState(null);

    const [ viewPath, setViewPath ] = useState(null);

    const [ loading, setLoading ] = useState(false);
    const [ columns, setColumns ] = useState(null);
    const [ tableRows, setTableRows ] = useState(null);

    // save table config
    const [ config, setConfig ] = useState(null);

    // set if editable and modal type. by default it is editable and modal
    const [ editable, setEditable ] = useState(true);
    const [ modalDefault ] = useState(true);
    const [ modal, setModal ] = useState(true);
    
    const [ selectedNotification, setSelectedNotification ] = useState(null);

    // for the modal
    const [ selectedNotificationModal, setSelectedNotificationModal ] = useState(null);
    const [ showNotificationModal, setShowNotificationModal ] = useState(false);

    // to check if the user can manage notifications
    //const [ notificationsManageGranted, setNotificationsManageGranted ] = useState(false);

    const { sxTabs, sx, sxIcon } = styleTabs();
    const tabs = ['list','manage'];
    const [ tab, setTab ] = useState(getCurrentTab(router,tabs));    

    const handleTabChange = (event, newValue) => {
        
        setTab(newValue);
        const url = getNewUrl(router,table,tabs,newValue);
        router.navigate(url);
        //window.history.pushState({path:url},'',url);

        // if clicking tab, go out of item details if there
        setSelectedNotification(null);

        // if going back to list, refresh notifications just in case it has been changed
        if(response?.data?.manage === 1 && newValue !== 'manage'){
            handleGet(router.pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal);
        }
    };

    // detect tab reclick
    const handleTabReClick = (clickedTabValue) => {
    
        // if clicking on same tab
        if(clickedTabValue === tab){
            // if clicking tab, go out of item details if there
            setSelectedNotification(null);
        }
    }

    // to open notification on single page
    // keep even if modal because if we go to /view/N the back button uses this
    const handleNotification = (notification) => {
        
        setSelectedNotification(notification);

        // change path
        let url;
        if(notification){
            url = '/'+table+'/view/'+notification?.id;
        }
        else{
            url = '/'+table;
        }
        
        router.navigate(url);
        //window.history.pushState({path:url},'',url);

        setViewPath('view');

        //setShowNotification(true);
    }

    // to open notification on modal
    const handleNotificationModal = (notification) => {
        setSelectedNotificationModal(notification);
        setShowNotificationModal(true);
    }

    const resetData = () => {
        if(columns && Object.keys(columns).length > 0){
            setColumns(null);
        }

        if(tableRows && tableRows.length > 0){
            setTableRows(null);
        }
    };

    // if changing the path, check if view item
    useEffect(() => {

        if(router.pathname){   

            // go back to main tab
            setTab(getCurrentTab(router,tabs));

            // if clicking on menu and in item details, go back to list
            const { viewType } = checkViewItem(router);
            if(!viewType){
                setSelectedNotification(null);
            }
        }

    }, [router.pathname]);

    // get data
    useEffect(() => {

        // set null to get the new item if on item view
        setSelectedNotification(null);

        // check if view item details
        const { viewType, viewItemId } = checkViewItem(router);
        setViewPath(viewType);

        // fullview does not have back button, so get only 1 row, not all
        var itemId = null;
        if(viewType === 'fullview'){
            itemId = viewItemId;
        }

        handleGet(router.pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal,setSelectedNotification,itemId);

    }, []);

    // to retrieve the data
    const handleGet = (pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal,setSelectedNotification = null,itemId = null) => {
        getData(pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal,setSelectedNotification,itemId);
    }

    // refresh data    
    const handleRefresh = () => {        
        handleGet(router.pathname,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable,modalDefault,setModal);
    }

    // add/edit a row
    // if inline edition, we use setSavedEnabled
    // if modal edition, we use handleCloseItemModal
    const handleEdit = (action,api,node,data,setSavedEnabled = null,setAddDisabled = null,handleCloseItemModal = null) => {        
        setData(action,router.pathname,table,api,node,modal,data,columns,setTableRows,setSavedEnabled,setAddDisabled,handleCloseItemModal);
    }

    // delete a row
    const handleDelete = (selectedRows,api,handleCloseItemModal = null) => {
        deleteData(router.pathname,table,selectedRows,api,columns,setTableRows,handleCloseItemModal);
    }

    // scroll to top
    useEffect(() => {
        
        /*
        scroll to title
        if(selectedNotification && containerRef.current){
            containerRef.current.scrollIntoView(
                //{ 
                    //behavior: 'smooth',
                    //block: 'start'
                //}
            );
        }
        */

        // scroll to top
        if(selectedNotification){
            window.scrollTo(/*{
                top: 0,
                behavior: 'smooth'
            }*/);
        }

    },[selectedNotification]);

    return (
        <>
        {
            showNotificationModal &&
            <Suspense>
                <NotificationModal 
                    t={t}
                    open={showNotificationModal} 
                    setOpen={setShowNotificationModal} 
                    notification={selectedNotificationModal}
                    manage={response?.data?.manage ? true : false}
                    fullScreen={fullScreen}
                />
            </Suspense>
        }
        <div className='tabs-container'>
            {
                (response?.data?.manage === 1 && !selectedNotification) &&
                <Suspense>
                    <Tabs   
                        id="tabs"                
                        sx={sxTabs}                
                        value={tab} 
                        onChange={handleTabChange}
                        variant="scrollable"                    
                    >
                        <Tab 
                            icon={<ListIcon sx={sxIcon}/>} 
                            value="list" 
                            label={t(table+'.tabs.list')} 
                            sx={sx}
                            onClick={() => handleTabReClick('list')}
                        />
                        <Tab 
                            icon={<ManageIcon sx={sxIcon}/>}
                            value="manage" 
                            label={t(table+'.tabs.manage')} 
                            sx={sx}
                            onClick={() => handleTabReClick('manage')}
                        />
                    </Tabs>
                </Suspense>
            }

            {
                selectedNotification !== null ?
                    <div ref={containerRef} className={`rounded md:px-4 px-2 pb-2 border bg-gray-50 border border-gray-200 mt-2 ${viewPath === 'fullview' && 'fullview'}`}>
                        {
                            selectedNotification && Object.keys(selectedNotification)?.length > 0 ?
                            <div className='mt-2 pb-8'> 
                                <div className="text-xl font-bold text-center">
                                    {selectedNotification?.titulo}
                                </div>
                                <div className="text-xs text-center border-b pb-2">
                                    {date(selectedNotification?.fecha_publicacion,true)}
                                </div>
                                {/*
                                <div className="text-center border-b py-1">
                                    {
                                        selectedNotification?.roles?.length > 0 ?
                                            selectedNotification?.roles?.map((role, indexChip) => (
                                                <span key={indexChip}>
                                                    <Chip                                                                 
                                                        label={role?.name} 
                                                        className="m-1"
                                                    />
                                                </span>
                                            ))
                                        :
                                            <span key={'empty_'+index}>
                                                <Chip                                                             
                                                    sx={{
                                                        marginX:'2px',
                                                        backgroundColor:'rgb(129,0,0,1)',
                                                        color:'#FFFFFF',
                                                        fontWeight:'bold'
                                                    }}
                                                    label={t('notifications.empty.roles')}
                                                    className="m-1"
                                                />
                                            </span>
                                    }
                                </div>
                                */}
                                <div className="mt-4 md:px-2">                                    
                                    <div dangerouslySetInnerHTML={{__html: html(selectedNotification?.descripcion)}}></div>
                                </div>
                            </div>
                            :
                                <div className='my-4 text-center'>
                                    {t('error.dataretrievingerror')}
                                </div>
                        }
                        {
                            viewPath === 'view' &&
                            <div className="flex justify-center items-center mx-1 mt-4 border-t border-gray-200 pt-2">                                        
                                <Button
                                    onClick={() => handleNotification(null)}
                                    text={t('actions.back.button')}
                                    type='back'                                                
                                />                                        
                            </div>
                        }
                    </div>
                :
            
                <div className='mt-8'>
                {
                    (response?.data?.manage === 1 && !selectedNotification) && tab === 'manage' ?
                        (columns && Object.keys(columns).length > 0) &&
                        (tableRows) &&
                        <Suspense>
                            <Table   
                                api={api}
                                setApi={setApi} 
                                apiUrl={apiUrl}
                                loading={loading}
                                t={t}
                                language={language}
                                user={user}     
                                token={token}
                                handleLogin={handleLogin}                                                 
                                router={router}
                                table={table}
                                config={config}
                                modal={modal}
                                rows={tableRows}
                                columns={columns}
                                response={response}                                
                                handleEdit={handleEdit}
                                handleDelete={handleDelete}    
                                handleRefresh={handleRefresh}                    
                                editable={editable}                            
                                sendMessage={sendMessage} 
                                fullScreen={fullScreen}  
                                fullPage={fullPage} 
                                setFullPage={setFullPage}
                                openUrlModal={openUrlModal}
                            />
                        </Suspense>
                    :
                        <div className=''>
                            {
                                tableRows && tableRows?.length > 0 ?
                                    <Suspense>
                                        <Notification
                                            items={tableRows}
                                            handleView={handleNotificationModal}
                                        />
                                    </Suspense>                                                      
                                :
                                    tableRows !== null &&
                                    <div className='text-center'>
                                        {t('notifications.empty.none')}
                                    </div>
                            }                                                    
                        </div>
                }
                </div>
            }
        </div>
        </>
    );
}